import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Video, FileText, Target, ExternalLink, Award, CheckCircle, Star, CreditCard, Building2, Infinity } from 'lucide-react';

const Ciro = () => {
  const products = [
    {
      title: 'CIRE Exam Study Book',
      subtitle: '700-Page Complete CIRO Guide',
      description: 'Comprehensive CIRE exam study guide covering all Canadian Investment Regulatory Organization topics with detailed explanations, examples, and practice questions for securities licensing.',
      icon: BookOpen,
      link: 'https://a.co/d/bKFZGqL',
      linkText: 'Buy on Amazon',
      color: 'blue',
    },
    {
      title: 'RSE Exam Study Book',
      subtitle: '780-Page Complete RSE Guide',
      description: 'The most thorough RSE exam preparation guide available. Master every CIRO concept with clear explanations, regulatory frameworks, and extensive practice questions.',
      icon: FileText,
      link: 'https://a.co/d/4bUB4s0',
      linkText: 'Buy on Amazon',
      color: 'blue',
    },
    {
      title: 'CIRE & RSE Video Course',
      subtitle: 'Complete CIRO Video Training',
      description: 'Instructor-led video courses, podcasts, comprehensive question banks, full mock exams, and study materials. Everything you need to pass your CIRO certification exams.',
      icon: Video,
      link: 'https://www.vittafinance.ca/courses',
      linkText: 'Buy on Vitta Finance',
      color: 'amber',
    },
  ];

  const features = [
    'Complete CIRO curriculum and regulatory framework coverage',
    'Step-by-step video explanations from industry expert',
    'Extensive question banks with detailed answer explanations',
    'Full-length CIRE and RSE mock exams',
    'Podcast summaries for on-the-go CIRO exam learning',
    'Regular content updates reflecting regulatory changes',
  ];

  // Structured data for CIRO courses
  const ciroSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "CIRO Exam Preparation - CIRE and RSE Certification",
    "description": "Complete CIRO exam preparation including CIRE (Canadian Investment Regulatory Exam) and RSE (Registered Securities Exam) certification courses for Canadian securities professionals",
    "provider": {
      "@type": "Person",
      "name": "Harry Vadalkar"
    },
    "educationalLevel": "Professional",
    "about": ["CIRO Exam", "CIRE Certification", "RSE Exam", "Canadian Securities Licensing", "Investment Dealer Compliance"],
    "teaches": ["CIRO regulations", "Securities compliance", "Investment dealer operations", "Canadian securities law"]
  };

  return (
    <div className="min-h-screen" data-testid="ciro-page">
      <Helmet>
        <title>CIRO Exam Prep 2026 | CIRE Exam | RSE Certification | ISE Exam | Canadian Securities Licensing | Vitta Finance</title>
        <meta name="description" content="Pass your CIRO exams with comprehensive CIRE, RSE, and ISE exam preparation. 700+ page study guides, video courses, full-length mock exams, question banks, and podcasts. Expert CIRO certification training for Canadian Investment Regulatory Organization licensing. Industry-leading pass rate. Vitta Finance courses by Harry Vadalkar." />
        <meta name="keywords" content="CIRO exam prep, CIRO exam preparation 2026, CIRO certification, CIRO exam pass rate, CIRE exam, CIRE exam prep, CIRE study guide, CIRE mock exams, CIRE question bank, CIRE practice questions, CIRE exam tips, RSE exam, RSE exam prep, RSE certification, RSE study guide, RSE mock exams, RSE question bank, RSE element wise question bank, ISE exam, ISE mock exams, Institutional Securities Exam, Canadian Investment Regulatory Organization, CIRO course, CIRO training, CIRO regulations, CIRO compliance, CIRO registration categories, CIRO continuing education, securities licensing Canada, investment dealer exam, investment representative exam, vitta finance, vittafinance.ca, pass CIRE exam, pass RSE exam, pass ISE exam, CIRO certification course, CIRO exam book, CIRE study book, RSE study book, Harry Vadalkar CIRO, Canadian securities compliance, IIROC exam, dealer representative exam" />
        <link rel="canonical" href="https://harryvadalkar.ca/ciro" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://harryvadalkar.ca/ciro" />
        <meta property="og:title" content="CIRO Exam Prep | CIRE & RSE Certification Courses" />
        <meta property="og:description" content="Pass your CIRO exams with comprehensive CIRE, RSE, and ISE exam preparation. Expert CIRO certification training." />
        <meta property="og:image" content="https://vittafinance.ca/images/ciro-course-og.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CIRO Exam Prep | CIRE & RSE Certification Courses" />
        <meta name="twitter:description" content="Pass your CIRO exams with comprehensive CIRE, RSE, and ISE exam preparation." />
        <meta property="og:description" content="Complete CIRO exam preparation. CIRE and RSE certification courses with study guides, video training, and mock exams. Industry-leading pass rate." />
        <script type="application/ld+json">
          {JSON.stringify(ciroSchema)}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/30 to-[#020617]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full mb-6">
                <Award className="w-4 h-4" />
                <span className="font-medium">CIRO Exam Preparation</span>
              </div>
              <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
                Pass Your <span className="text-blue-400">CIRO Exams</span>
              </h1>
              <p className="text-xl text-slate-300 mb-6">
                Comprehensive CIRE and RSE exam preparation through vittafinance.ca. Books, video courses, and practice materials to ensure your success.
              </p>
              <a
                href="https://www.vittafinance.ca/courses"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-vitta-link"
                className="inline-flex items-center text-blue-400 hover:text-blue-300 font-semibold text-lg"
              >
                Visit vittafinance.ca
                <ExternalLink className="w-5 h-5 ml-2" />
              </a>
            </div>

            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="glass rounded-2xl p-8">
                <Target className="w-16 h-16 text-blue-400 mb-6" />
                <h3 className="font-heading text-2xl font-bold text-white mb-4">What is CIRO?</h3>
                <p className="text-slate-300 mb-4">
                  CIRO (Canadian Investment Regulatory Organization) is the national self-regulatory organization that oversees investment dealers and mutual fund dealers in Canada.
                </p>
                <p className="text-slate-400">
                  The CIRE and RSE exams are essential for professionals seeking to work in Canada's securities industry.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Pricing Section */}
      <section className="py-20 bg-gradient-to-b from-slate-900/50 to-slate-900" data-testid="corporate-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full mb-6">
              <Building2 className="w-4 h-4" />
              <span className="font-medium">Corporate & Individual Pricing</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              CIRO Exam <span className="text-amber-400">Courses</span>
            </h2>
            <p className="text-slate-300 text-lg max-w-3xl mx-auto mb-4">
              One-time payment per candidate with lifetime access to all study materials. Does not include printed book.
            </p>
            <p className="text-amber-400 font-semibold mt-4">Valid until December 31, 2027</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* CIRE Course */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all">
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded">CIRE</span>
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-2">
                Canadian Investment Regulatory Exam
              </h3>
              
              <div className="space-y-4 my-6">
                <div className="border-b border-slate-700 pb-4">
                  <div className="flex items-start gap-3">
                    <Video className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Online Video Course</p>
                      <p className="text-slate-400 text-sm">Lifetime access — Video tutorials, question bank & mock exams</p>
                    </div>
                  </div>
                </div>
                <div className="border-b border-slate-700 pb-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Online Interactive Quick Study Guide</p>
                      <p className="text-slate-400 text-sm">Lifetime access — Interactive digital study resource</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Total per Candidate</p>
                      <p className="text-slate-400 text-sm">Lifetime access — one-time payment</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-white">$110.00 <span className="text-lg font-normal text-slate-400">CAD</span></p>
              </div>

              <div className="space-y-3">
                <a
                  href="https://vittafinance.mykajabi.com/offers/nUSRwRWz/checkout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-lg text-center transition-colors"
                >
                  Pay Now
                </a>
                <a
                  href="https://vittafinance.ca/corporate/cire-access"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 rounded-lg text-center transition-colors"
                >
                  After Payment Access
                </a>
              </div>
            </div>

            {/* RSE Course */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:border-amber-500/50 transition-all">
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded">RSE</span>
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-2">
                Retail Securities Exam
              </h3>
              
              <div className="space-y-4 my-6">
                <div className="border-b border-slate-700 pb-4">
                  <div className="flex items-start gap-3">
                    <Video className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Online Video Course</p>
                      <p className="text-slate-400 text-sm">Lifetime access — Video tutorials, question bank & mock exams</p>
                    </div>
                  </div>
                </div>
                <div className="border-b border-slate-700 pb-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Online Interactive Quick Study Guide</p>
                      <p className="text-slate-400 text-sm">Lifetime access — Interactive digital study resource</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Total per Candidate</p>
                      <p className="text-slate-400 text-sm">Lifetime access — one-time payment</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-white">$125.00 <span className="text-lg font-normal text-slate-400">CAD</span></p>
              </div>

              <div className="space-y-3">
                <a
                  href="https://vittafinance.mykajabi.com/offers/JiaLfkkE/checkout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold py-3 rounded-lg text-center transition-colors"
                >
                  Pay Now
                </a>
                <a
                  href="https://vittafinance.ca/corporate/rse-access"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 rounded-lg text-center transition-colors"
                >
                  After Payment Access
                </a>
              </div>
            </div>

            {/* Derivatives Course */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:border-purple-500/50 transition-all">
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded">DERIVATIVES</span>
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-2">
                CIRO Derivative Exam
              </h3>
              
              <div className="space-y-4 my-6">
                <div className="border-b border-slate-700 pb-4">
                  <div className="flex items-start gap-3">
                    <Video className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Online Video Course</p>
                      <p className="text-slate-400 text-sm">Lifetime access — Video tutorials, question bank & mock exams</p>
                    </div>
                  </div>
                </div>
                <div className="border-b border-slate-700 pb-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Online Interactive Quick Study Guide</p>
                      <p className="text-slate-400 text-sm">Lifetime access — Interactive digital study resource</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Total per Candidate</p>
                      <p className="text-slate-400 text-sm">Lifetime access — one-time payment</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-white">$125.00 <span className="text-lg font-normal text-slate-400">CAD</span></p>
              </div>

              <div className="space-y-3">
                <a
                  href="https://vittafinance.mykajabi.com/offers/eoHHKFZN/checkout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold py-3 rounded-lg text-center transition-colors"
                >
                  Pay Now
                </a>
                <a
                  href="https://vittafinance.ca/corporate/derivatives-access"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 rounded-lg text-center transition-colors"
                >
                  After Payment Access
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Study <span className="text-blue-400">Materials</span>
            </h2>
            <p className="text-slate-400 text-lg">
              Everything you need to pass your CIRO exams
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div
                key={index}
                className="glass rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 flex flex-col"
              >
                <product.icon className={`w-12 h-12 ${product.color === 'amber' ? 'text-amber-400' : 'text-blue-400'} mb-6`} />
                <h3 className="font-heading text-2xl font-bold text-white mb-2">{product.title}</h3>
                <p className={`${product.color === 'amber' ? 'text-amber-400' : 'text-blue-400'} font-medium mb-4`}>
                  {product.subtitle}
                </p>
                <p className="text-slate-400 mb-8 flex-grow">{product.description}</p>
                <a
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`ciro-product-${index}`}
                  className={`inline-flex items-center justify-center font-semibold px-6 py-4 rounded-xl transition-all ${
                    product.color === 'amber'
                      ? 'bg-amber-500 hover:bg-amber-600 text-black'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  {product.linkText}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-900/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl font-bold text-white mb-6">
                What's <span className="text-amber-400">Included</span>
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Our comprehensive CIRO exam prep includes everything you need to succeed.
              </p>
              <ul className="space-y-4">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-4">
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                    <span className="text-slate-200 text-lg">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="glass rounded-2xl p-8">
              <div className="text-center">
                <Star className="w-16 h-16 text-amber-400 mx-auto mb-6" />
                <h3 className="font-heading text-2xl font-bold text-white mb-4">
                  Trusted by Thousands
                </h3>
                <p className="text-slate-300 mb-6">
                  Join the community of successful candidates who passed their CIRO exams with our materials.
                </p>
                <div className="flex justify-center gap-8">
                  <div>
                    <p className="text-3xl font-bold text-white">1,500+</p>
                    <p className="text-slate-400">Pages of Content</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-white">Industry</p>
                    <p className="text-slate-400">Leading Pass Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" data-testid="ciro-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to <span className="text-blue-400">Start Preparing</span>?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Visit vittafinance.ca for the complete CIRO exam preparation experience.
          </p>
          <a
            href="https://www.vittafinance.ca/courses"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ciro-final-cta"
            className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-black font-bold px-10 py-4 rounded-full transition-all shadow-lg hover:shadow-amber-500/30 text-lg"
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            Visit Vitta Finance
          </a>
        </div>
      </section>
    </div>
  );
};

export default Ciro;